import React, { useEffect, useMemo, useState } from 'react';
import FeeSection from './FeeSection';
import FlagIssueModal from './FlagIssueModal';
import ParentSignalsBlock from './ParentSignalsBlock';
import SchoolHighlights from './SchoolHighlights';
import {
  projectFees, buildEnrichedPriorityPills, buildWhyChosenNarrative,
  displayName, shortDisplayName, friendlySchoolLocation, ageAppropriateCurriculum,
} from '../../lib/reportUtils';

// Interactive-star component with half-star rounding.
const StarRow = ({ rating }) => {
  const n = Math.round((rating || 0) * 2) / 2;
  return (
    <span className="text-amber-400" aria-label={`Rating ${rating}`}>
      {[1, 2, 3, 4, 5].map((i) => (
        <span key={i} className={i <= n ? 'text-amber-400' : 'text-[#E8DED3]'}>
          ★
        </span>
      ))}
    </span>
  );
};

// ----- Helpers -----

const curriculumShort = (curriculum) => {
  const c = (curriculum || '').toUpperCase();
  // Cambridge first — for dual-curriculum schools (e.g. "Cambridge IGCSE & IB")
  // the IGCSE/A-Level track is the primary identity for secondary parents.
  if (c.includes('CAMBRIDGE') || c.includes('IGCSE') || c.includes('BRITISH')) return 'Cambridge pathway';
  if (c.includes('IB') || c.includes('INTERNATIONAL BACCALAUREATE')) return 'IB excellence';
  if (c.includes('AMERICAN')) return 'American curriculum';
  if (c.includes('AUSTRALIAN')) return 'Australian curriculum';
  return 'Strong academic pathway';
};

// "IB + holistic development" style subtitle
const alignmentSubtitle = (school, priorityLabels) => {
  const short = curriculumShort(school.curriculum_framework).split(' ')[0]; // "IB", "Cambridge", ...
  const firstPriority = priorityLabels?.[0];
  if (firstPriority) {
    // Lowercase the priority for natural flow
    return `${short} + ${firstPriority.toLowerCase()}`;
  }
  return curriculumShort(school.curriculum_framework);
};

// Build a plain-English synthesis paragraph from available reviews.
const synthesizeReviews = (allReviews, schoolName) => {
  const n = allReviews.length;
  if (n === 0) {
    return `Community reviews are still being collected for ${schoolName}. Be the first to share your experience on Switch Schools.`;
  }
  const ratings = allReviews.map((r) => r.rating || 0);
  const avg = ratings.reduce((a, b) => a + b, 0) / n;
  const best = [...allReviews].sort((a, b) => (b.rating || 0) - (a.rating || 0))[0];
  const worst = [...allReviews].sort((a, b) => (a.rating || 0) - (b.rating || 0))[0];
  const positive = (best.text || '').replace(/^["']+|["']+$/g, '').trim();
  const positiveSnippet = positive.length > 220 ? `${positive.slice(0, 220)}…` : positive;

  const opening = `Based on ${n} recent parent review${n !== 1 ? 's' : ''} across Switch Schools and Google, the overall rating is ${avg.toFixed(1)} out of 5.`;

  let concernSentence = '';
  if (n > 1 && (worst.rating || 0) <= 3 && worst !== best) {
    const cw = (worst.text || '').replace(/^["']+|["']+$/g, '').trim();
    if (cw) {
      const snippet = cw.length > 140 ? `${cw.slice(0, 140)}…` : cw;
      concernSentence = ` At least one reviewer raised concerns — noting: "${snippet}"`;
    }
  }

  if (positiveSnippet) {
    return `${opening} Recent praise: "${positiveSnippet}"${concernSentence}`;
  }
  return opening;
};

// Single review card
const ReviewCard = ({ review }) => (
  <div
    className="rounded-xl bg-white border border-[#E8DED3] p-4"
    data-testid={`review-card-${review.source}-${review.key}`}
  >
    <div className="flex items-start justify-between gap-3 mb-2">
      <span className="font-semibold text-[#2C2823] text-sm">{review.author}</span>
      <span className="text-base whitespace-nowrap"><StarRow rating={review.rating} /></span>
    </div>
    <p className="text-sm text-[#2C2823] leading-relaxed italic">
      {review.text && review.text.length > 240 ? `${review.text.slice(0, 240)}…` : review.text}
    </p>
    <p className="text-xs text-[#9B8F88] mt-3 uppercase tracking-wider font-semibold">
      {review.source === 'community' ? 'Switch Schools · Community' : 'Google Reviews'}
      {review.relative_time ? ` · ${review.relative_time}` : ''}
    </p>
  </div>
);

// Main component
const TopSchoolCard = ({
  school,
  currentGrade,
  currentSchoolName,
  currentSchoolFee,
  schoolsList,
  summary,
  apiUrl,
  priorityLabels = [],
  reportId,
  highlightsTier = 'full',
  justUnlocked = false,
}) => {
  const [reviews, setReviews] = useState(null);
  const [reviewsLoading, setReviewsLoading] = useState(true);
  const [flagOpen, setFlagOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    if (!school?.id || !apiUrl) return;
    (async () => {
      try {
        const r = await fetch(`${apiUrl}/api/schools/${school.id}/reviews`);
        const data = await r.json();
        if (!cancelled) setReviews(data);
      } catch {
        if (!cancelled) setReviews(null);
      } finally {
        if (!cancelled) setReviewsLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [school?.id, apiUrl]);

  // Project recommended fees for the narrative fee corridor.
  const recRows = useMemo(
    () => projectFees(school?.annual_fees || {}, Math.max(1, currentGrade || 1), 11),
    [school?.annual_fees, currentGrade]
  );

  const enrichedPills = useMemo(() => buildEnrichedPriorityPills(summary), [summary]);
  const narrative = useMemo(
    () => buildWhyChosenNarrative(summary, school, recRows),
    [summary, school, recRows]
  );

  // Merge community + google, up to 4 for 2x2 grid
  const allReviews = useMemo(() => {
    const community = (reviews?.community?.reviews || []).map((r, i) => ({
      author: r.display_name || 'Anonymous parent',
      rating: r.rating,
      text: r.review_text,
      source: 'community',
      relative_time: null,
      key: `c-${i}`,
    }));
    const google = (reviews?.google?.reviews || []).map((r, i) => ({
      author: r.author || 'Google reviewer',
      rating: r.rating,
      text: r.text,
      source: 'google',
      relative_time: r.relative_time,
      key: `g-${i}`,
    }));
    return [...community, ...google].slice(0, 4);
  }, [reviews]);

  const synthesis = useMemo(
    () => synthesizeReviews(allReviews, shortDisplayName(school)),
    [allReviews, school]
  );

  const location = friendlySchoolLocation(school);

  const schoolNameBig = (displayName(school) || '').toUpperCase();
  const chipSubtitle = curriculumShort(school.curriculum_framework);
  const heroSubtitle = alignmentSubtitle(school, priorityLabels);

  const fromYear = Math.max(1, currentGrade || 1);
  const toYear = 11;

  return (
    <article
      className={
        'rounded-2xl bg-white overflow-hidden shadow-[0_2px_12px_rgba(44,40,35,0.05)] transition-all ' +
        (justUnlocked
          ? 'border-2 border-[#E85D35] ring-4 ring-[#E85D35]/20 animate-pulse-once'
          : 'border border-[#E8DED3]')
      }
      data-testid="top-school-card"
    >
      {/* ---------- Match header ---------- */}
      <header className="px-6 sm:px-8 py-6 border-b border-[#E8DED3]">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div className="flex-1 min-w-0">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#E85D35] mb-1.5 flex items-center gap-2">
              Your match
              {highlightsTier === 'full' && (
                <span
                  data-testid="full-report-pill"
                  className={
                    'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] ' +
                    'font-bold uppercase tracking-wider ' +
                    (justUnlocked
                      ? 'bg-emerald-100 text-emerald-800 border border-emerald-300 animate-bounce-subtle'
                      : 'bg-emerald-50 text-emerald-700 border border-emerald-200')
                  }
                >
                  Full report · unlocked
                </span>
              )}
            </p>
            <h2 className="text-2xl sm:text-3xl font-black uppercase tracking-tight text-[#2C2823] leading-tight">
              {schoolNameBig}
            </h2>
            <p className="text-sm text-[#6B6560] mt-1.5">Included with Basic Report (RM 4.99)</p>
          </div>
          <span
            className="shrink-0 inline-flex items-center px-4 py-2 rounded-full bg-[#2C2823] text-white text-xs font-bold uppercase tracking-wider"
            data-testid="match-chip"
          >
            Best match · {chipSubtitle}
          </span>
        </div>
      </header>

      {/* ---------- What was important to you + Why this school was chosen ---------- */}
      {(enrichedPills.length > 0 || narrative) && (
        <div
          className="px-6 sm:px-8 py-6 border-b border-[#E8DED3] bg-white"
          data-testid="priorities-and-why"
        >
          {enrichedPills.length > 0 && (
            <>
              <h3 className="text-base sm:text-lg font-bold text-[#2C2823] mb-3">
                What was important to you
              </h3>
              <div className="flex flex-wrap gap-2 mb-5" data-testid="enriched-priorities-pills">
                {enrichedPills.map((p) => (
                  <span
                    key={p}
                    className="inline-flex items-center px-3.5 py-1.5 rounded-full bg-[#FFF5EB] border border-[#E8DED3] text-sm font-semibold text-[#2C2823]"
                  >
                    {p}
                  </span>
                ))}
              </div>
            </>
          )}

          {narrative && (
            <div
              className="rounded-xl bg-[#F0F9F4] border border-emerald-100 p-5"
              data-testid="why-chosen-narrative"
            >
              <p className="text-sm font-bold text-emerald-800 uppercase tracking-wider mb-2">
                Why {shortDisplayName(school)} was chosen for you
              </p>
              <p className="text-[#2C2823] leading-relaxed">{narrative}</p>
            </div>
          )}
        </div>
      )}

      {/* ---------- Hero match ---------- */}
      <div className="px-6 sm:px-8 py-7 bg-[#FFF5EB] border-b border-[#E8DED3]">
        <div className="flex flex-col sm:flex-row items-start sm:items-center gap-6">
          <div className="flex items-center gap-5 shrink-0">
            <div
              className="w-24 h-24 sm:w-28 sm:h-28 rounded-full bg-[#E85D35] text-white flex items-center justify-center text-3xl sm:text-4xl font-black shadow-[0_6px_18px_rgba(232,93,53,0.25)]"
              data-testid="score-circle"
            >
              {school.match_percentage != null ? `${school.match_percentage}%` : '—'}
            </div>
            <div>
              <p className="font-bold text-[#2C2823]">Priority alignment</p>
              <p className="text-sm text-[#6B6560]">{heroSubtitle}</p>
            </div>
          </div>
          <div className="flex-1 sm:pl-8 sm:border-l sm:border-[#E8DED3]">
            <p className="text-lg font-bold text-[#2C2823]">
              {displayName(school)}
              {location ? ` (${location})` : ''}
            </p>
            {school.curriculum_framework && (
              <p className="text-sm text-[#6B6560] mt-1">
                {ageAppropriateCurriculum(school, fromYear)}
                {location ? ` · ${location} community` : ''}
              </p>
            )}
          </div>
        </div>
      </div>

      {/* ---------- Fee projection ---------- */}
      <div className="px-6 sm:px-8 py-6 border-b border-[#E8DED3]">
        <h3 className="text-base sm:text-lg font-bold text-[#2C2823] mb-4">
          Fee projection vs current school · Year {fromYear}–{toYear}
        </h3>
        <FeeSection
          schoolsList={schoolsList}
          recommendedSchool={school}
          currentSchoolName={currentSchoolName}
          currentGrade={currentGrade}
          currentFeeFromReport={currentSchoolFee}
        />
      </div>

      {/* ---------- School highlights (verified signals) ---------- */}
      <div className="px-6 sm:px-8 pt-2">
        <SchoolHighlights school={school} tier={highlightsTier} justUnlocked={justUnlocked} />
      </div>

      {/* ---------- What parents say (aggregated Q15/Q16) ---------- */}
      <div className="px-6 sm:px-8 py-6">
        <ParentSignalsBlock
          schoolId={school.id}
          schoolName={shortDisplayName(school)}
          apiUrl={apiUrl}
        />
      </div>

      {/* ---------- Flag an inaccuracy ---------- */}
      <div className="px-6 sm:px-8 pb-5 pt-1 flex justify-end">
        <button
          type="button"
          onClick={() => setFlagOpen(true)}
          className="text-xs font-semibold text-[#9B8F88] hover:text-[#E85D35] underline underline-offset-4 decoration-dotted"
          data-testid="flag-issue-link-top"
        >
          Notice something wrong?
        </button>
      </div>

      <FlagIssueModal
        open={flagOpen}
        onClose={() => setFlagOpen(false)}
        school={school}
        reportId={reportId}
        apiUrl={apiUrl}
      />
    </article>
  );
};

export default TopSchoolCard;
